import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { to: 'home', label: 'Home' },
  { to: 'about', label: 'About' },
  { to: 'achievements', label: 'Wins' },
  { to: 'gallery', label: 'Gallery' },
  { to: 'skills', label: 'Journey' },
  { to: 'sponsorship', label: 'Sponsor' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileOpen]);

  return (
    <>
      {/* Fixed Header Wrapper - Full Width Container */}
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
      >
        <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-5 md:px-6 pt-3 sm:pt-4 md:pt-5">
          <nav className="pointer-events-auto flex items-center justify-between gap-2 sm:gap-3">
            
            {/* LEFT - Logo Pill */}
            <Link
              to="home"
              smooth
              duration={800}
              onSetActive={() => setActiveSection('home')}
              className="cursor-pointer focus:outline-none flex-shrink-0"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center gap-2.5 pl-1.5 pr-3 sm:pr-4 py-1.5 rounded-full border transition-all duration-300 ${
                  scrolled
                    ? 'bg-[#0a0a0a]/95 backdrop-blur-2xl border-white/15 shadow-2xl shadow-black/50'
                    : 'bg-[#0a0a0a]/80 backdrop-blur-xl border-white/10 shadow-lg shadow-black/40'
                }`}
              >
                {/* V Logo Circle */}
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center relative overflow-hidden group shadow-md flex-shrink-0">
                  <span className="font-heading text-sm sm:text-base font-black text-black tracking-tighter">
                    V
                  </span>
                  <div className="absolute inset-0 bg-[#E10600] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="font-heading text-sm sm:text-base font-black text-white tracking-tighter">
                      V
                    </span>
                  </div>
                </div>

             
              </motion.div>
            </Link>

            {/* CENTER - Nav Links Pill (Desktop Only) */}
            <div
              className={`hidden lg:flex items-center gap-0.5 px-2 py-1.5 rounded-full border transition-all duration-300 ${
                scrolled
                  ? 'bg-[#0a0a0a]/95 backdrop-blur-2xl border-white/15 shadow-2xl shadow-black/50'
                  : 'bg-[#0a0a0a]/80 backdrop-blur-xl border-white/10 shadow-lg shadow-black/40'
              }`}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  smooth
                  duration={800}
                  offset={-100}
                  spy
                  onSetActive={() => setActiveSection(link.to)}
                  className="relative px-3 xl:px-4 py-1.5 text-[11px] xl:text-xs font-medium tracking-wider cursor-pointer group rounded-full focus:outline-none"
                >
                  {activeSection === link.to && (
                    <motion.div
                      layoutId="activePill"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      className="absolute inset-0 bg-white/[0.12] rounded-full border border-white/15"
                    />
                  )}

                  <span
                    className={`relative z-10 transition-colors duration-200 uppercase font-mono whitespace-nowrap ${
                      activeSection === link.to
                        ? 'text-white font-semibold'
                        : 'text-white/60 group-hover:text-white'
                    }`}
                  >
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>

            {/* RIGHT - CTA Pill (Desktop) + Hamburger (Mobile) */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Desktop Contact Button */}
              <Link
                to="sponsorship"
                smooth
                duration={800}
                offset={-100}
                className="hidden lg:block cursor-pointer"
              >
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`relative overflow-hidden bg-white text-black pl-4 pr-5 py-2.5 rounded-full text-[11px] xl:text-xs font-semibold tracking-wider uppercase font-mono group border transition-all duration-300 ${
                    scrolled ? 'border-white/20' : 'border-transparent'
                  }`}
                >
                  <div className="absolute inset-0 bg-[#E10600] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                  <span className="relative z-10 flex items-center gap-2 group-hover:text-white transition-colors duration-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E10600] group-hover:bg-white transition-colors duration-300 animate-pulse" />
                    Let's Talk
                  </span>
                </motion.div>
              </Link>

              {/* Mobile / Tablet Hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
                className={`lg:hidden relative z-50 w-11 h-11 rounded-full flex flex-col items-center justify-center gap-1 border transition-all focus:outline-none active:scale-95 ${
                  scrolled
                    ? 'bg-[#0a0a0a]/95 backdrop-blur-2xl border-white/15 shadow-2xl shadow-black/50'
                    : 'bg-[#0a0a0a]/80 backdrop-blur-xl border-white/10 shadow-lg shadow-black/40'
                }`}
              >
                <motion.span
                  animate={mobileOpen ? { rotate: 45, y: 5 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="w-4 h-[1.5px] bg-white rounded-full origin-center"
                />
                <motion.span
                  animate={mobileOpen ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="w-4 h-[1.5px] bg-white rounded-full"
                />
                <motion.span
                  animate={mobileOpen ? { rotate: -45, y: -5 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="w-4 h-[1.5px] bg-white rounded-full origin-center"
                />
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Fullscreen Navigation Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, clipPath: 'circle(0% at 95% 5%)' }}
            animate={{ opacity: 1, clipPath: 'circle(150% at 95% 5%)' }}
            exit={{ opacity: 0, clipPath: 'circle(0% at 95% 5%)' }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 lg:hidden flex flex-col bg-[#0a0a0a]/98 backdrop-blur-3xl h-[100dvh] overflow-hidden"
          >
            <div className="absolute top-1/4 right-0 w-72 h-72 bg-[#E10600]/15 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-[#E10600]/10 blur-3xl rounded-full pointer-events-none" />

            {/* Checkered pattern */}
            <div className="absolute top-20 left-6 grid grid-cols-4 gap-[2px] opacity-15 pointer-events-none">
              {[...Array(16)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 ${
                    (Math.floor(i / 4) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'
                  }`}
                ></div>
              ))}
            </div>

            <div className="relative flex-1 flex flex-col overflow-y-auto px-6 sm:px-8 pt-20 sm:pt-24 pb-6">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.35 }}
                className="mb-6 sm:mb-8 flex items-center justify-between pb-3 border-b border-white/10"
              >
                <span className="text-[10px] sm:text-xs tracking-[0.3em] text-white/40 uppercase font-mono">
                  Menu
                </span>
                <span className="text-[10px] sm:text-xs tracking-[0.3em] text-[#E10600] uppercase font-mono flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E10600] animate-pulse"></span>
                  Active
                </span>
              </motion.div>

              <div className="flex-1 flex flex-col justify-center space-y-1 sm:space-y-2 max-w-lg mx-auto w-full">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: 0.2 + i * 0.06, duration: 0.4 }}
                    className="w-full"
                  >
                    <Link
                      to={link.to}
                      smooth
                      duration={800}
                      offset={-100}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between py-3 sm:py-4 border-b border-white/5 hover:border-[#E10600]/50 font-heading text-3xl sm:text-4xl md:text-5xl tracking-wide text-white/80 hover:text-white active:text-[#E10600] cursor-pointer transition-all w-full group"
                    >
                      <span className="flex items-center gap-3 sm:gap-4 group-hover:translate-x-2 transition-transform duration-300 font-bold uppercase">
                        <span className="text-xs sm:text-sm font-mono text-[#E10600]/60 group-hover:text-[#E10600] transition-colors">
                          0{i + 1}
                        </span>
                        {link.label}
                      </span>
                      <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-white/10 group-hover:border-[#E10600] flex items-center justify-center transition-all">
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4 text-white/40 group-hover:text-[#E10600] group-hover:translate-x-0.5 transition-all"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="pt-6 mt-6 border-t border-white/10 space-y-3 max-w-lg mx-auto w-full"
              >
                <Link
                  to="sponsorship"
                  smooth
                  duration={800}
                  offset={-100}
                  onClick={() => setMobileOpen(false)}
                  className="w-full flex items-center justify-center gap-3 bg-[#E10600] hover:bg-[#c10500] text-white py-3.5 sm:py-4 rounded-full text-xs sm:text-sm font-mono font-bold uppercase tracking-widest shadow-xl shadow-[#E10600]/20 active:scale-[0.98] transition-all"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  Sponsor / Contact
                </Link>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <a
                    href="mailto:vishwas@racing.com"
                    className="flex flex-col items-start p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/20 transition-all"
                  >
                    <span className="text-[9px] tracking-[0.2em] text-white/40 uppercase font-mono mb-1">
                      Email
                    </span>
                    <span className="text-xs text-white font-mono truncate w-full">
                      vishwas@racing.com
                    </span>
                  </a>
                  <a
                    href="tel:+919876543210"
                    className="flex flex-col items-start p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/20 transition-all"
                  >
                    <span className="text-[9px] tracking-[0.2em] text-white/40 uppercase font-mono mb-1">
                      Phone
                    </span>
                    <span className="text-xs text-white font-mono">
                      +91 98765 43210
                    </span>
                  </a>
                </div>

                <div className="flex items-center justify-center gap-2 pt-3">
                  <div className="w-6 h-[1px] bg-white/20"></div>
                  <span className="text-[9px] tracking-[0.3em] text-white/40 uppercase font-mono">
                    Vishwas S.D. Racing
                  </span>
                  <div className="w-6 h-[1px] bg-white/20"></div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;